import React, {
  forwardRef,
  ReactNode,
  Ref,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import rrnIcon from '../../../assets/icons/icon_rrn.svg';
import { slotInputStyle } from '../../styledComponentStyle';
import { ColoredIcon } from '../Icon';

import { SlotWrapper, SlotWrapperProps } from './SlotWrapper';
import { useEnrollSlotRef } from './hooks';
import { SlotRef } from './types';

export const StyledTextField = styled.input<{
  type?: 'text' | 'password' | 'tel';
  $padding?: string;
  $fullWidth?: boolean;
  $textAlign?: string;
}>`
  ${slotInputStyle};
  box-sizing: border-box;
  padding: 9px
    ${p => (p.$textAlign === 'center' ? '4px' : p.$padding ?? '24px')};
  border: 1px solid #e7e7e7;
  border-radius: 4px;
  font-size: 14px;
  color: ${p => p.theme.colors.black1};
  text-align: ${p => p.$textAlign ?? 'justify'};
  &::placeholder {
    opacity: 0.5;
  }
  &:focus::placeholder {
    color: transparent;
  }
`;

export const MobileFullTextField = styled(StyledTextField)`
  max-width: none;
  height: 50px;
`;

interface TextFieldSlotProps extends SlotWrapperProps {
  type?: 'text' | 'password' | 'tel';
  fullwidth?: boolean;
  textAlign?: string;
  padding?: string;
  readOnly?: boolean;
  maxLength?: number;
  placeholder?: string;
  value?: string;
  disabled?: boolean;
  onChange?: (e: string) => void;
  onBlur?: () => void;
  children?: ReactNode;
}

export const TextFieldSlot = forwardRef(
  (
    {
      type = 'text',
      fullwidth,
      textAlign,
      padding,
      readOnly,
      maxLength,
      placeholder,
      value,
      disabled,
      onChange,
      onBlur,
      children,
      ...slotWrapperProps
    }: TextFieldSlotProps,
    ref: Ref<SlotRef>,
  ) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef(null);
    useEnrollSlotRef({ ref, wrapperRef, inputs: [inputRef.current] });

    return (
      <SlotWrapper ref={wrapperRef} {...slotWrapperProps}>
        <StyledTextField
          ref={inputRef}
          type={type}
          $fullWidth={fullwidth}
          $textAlign={textAlign}
          $padding={padding}
          maxLength={maxLength}
          readOnly={readOnly}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          onChange={e => onChange?.(e.currentTarget.value)}
          onBlur={onBlur}
        />
        {children}
      </SlotWrapper>
    );
  },
);

const RrnTextField = styled(StyledTextField)`
  padding: 7px 15px;
  max-width: 89px;
  min-width: auto;

  @media (max-width: 420px) {
    padding: 7px 5px;
    max-width: 67px;
  }
`;
const RrnDash = styled.span`
  font-size: 14px;
  margin: 0px 6px;
`;

export const RrnSlot = forwardRef(
  (
    {
      label,
      onChange,
      value,
    }: Pick<TextFieldSlotProps, 'label' | 'onChange' | 'value'>,
    ref: Ref<SlotRef>,
  ) => {
    const wrapperRef = useRef(null);
    const formerInputRef = useRef(null);
    const latterInputRef = useRef(null);
    useEnrollSlotRef({
      ref,
      wrapperRef,
      inputs: [formerInputRef.current, latterInputRef.current],
    });

    const [former, setFormer] = useState('');
    const [latter, setLatter] = useState('');

    useEffect(() => {
      onChange?.(`${former}${latter ? `-${latter}` : ''}`);
    }, [former, latter, onChange]);

    useEffect(() => {
      const [f, l] = value?.split('-') ?? [];
      if (f && /\[0-9]{0,6}/.test(f)) {
        setFormer(f.slice(0, 6));
      }
      if (l && /\[0-9]{0,7}/.test(l)) {
        setLatter(l.slice(0, 7));
      }
    }, [value]);

    return (
      <SlotWrapper
        wrap
        icon={
          <ColoredIcon
            width="24px"
            height="24px"
            imageUrl={rrnIcon}
            imageWidth="18px"
            color={'var(--HScan_Light_Blue, #2e5be0)'}
          />
        }
        label={label}>
        <span>
          <RrnTextField
            $textAlign="center"
            maxLength={6}
            placeholder="000000"
            value={former}
            onChange={e => setFormer(e.currentTarget.value)}
          />
          <RrnDash>-</RrnDash>
          <RrnTextField
            type="password"
            $textAlign="center"
            maxLength={7}
            placeholder="0000000"
            value={latter}
            onChange={e => setLatter(e.currentTarget.value)}
          />
        </span>
      </SlotWrapper>
    );
  },
);

const StyledMultiLineTextField = styled.textarea<{
  $textAlign?: string;
  $fullWidth?: boolean;
}>`
  ${slotInputStyle};
  box-sizing: border-box;
  padding: 9px 24px;
  border: 1px solid #e7e7e7;
  border-radius: 4px;
  font-size: 14px;
  color: var(--HPACS_Text_Light_Grey, #a3a6ab);
  text-align: ${p => p.$textAlign ?? 'justify'};
  resize: none;

  &:focus::placeholder {
    color: transparent;
  }
`;

interface MultiLineTextFieldSlotProps extends SlotWrapperProps {
  fullwidth?: boolean;
  textAlign?: string;
  maxLength?: number;
  placeholder?: string;
  value?: string;
  onChange?: (e: string) => void;
}
export const MultiLineTextFieldSlot = forwardRef(
  (
    {
      fullwidth,
      textAlign,
      maxLength,
      placeholder,
      value,
      onChange,
      ...slotWrapperProps
    }: MultiLineTextFieldSlotProps,
    ref: Ref<SlotRef>,
  ) => {
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    useEnrollSlotRef({ ref, wrapperRef, inputs: [inputRef.current] });

    return (
      <SlotWrapper ref={wrapperRef} {...slotWrapperProps}>
        <StyledMultiLineTextField
          ref={inputRef}
          rows={5}
          $fullWidth={fullwidth}
          $textAlign={textAlign}
          maxLength={maxLength}
          placeholder={placeholder}
          value={value}
          onChange={e => onChange?.(e.currentTarget.value)}
        />
      </SlotWrapper>
    );
  },
);
